import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsConge, useNonPrestes } from '@/api/hooks';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { dateFr, dateHeureFr } from '@/lib/utils';

const LIBELLE_ETAT = { PREPARE: 'Préparé', TRANSMIS: 'Transmis à la paie', CONFIRME: 'Confirmé par la paie' } as const;

/** État mensuel des jours non prestés (point 11) : préparé et transmis par les RH, confirmé par la paie. */
export function NonPrestes() {
  const { mois: moisParam } = useParams();
  const navigate = useNavigate();
  const u = useSession()!.utilisateur;
  const rh = u.profils.some((p) => p === 'DIRECTEUR_RH' || p === 'RESSOURCES_HUMAINES');
  const paie = u.profils.includes('PAIE');
  const auj = new Date();
  const mois = moisParam ?? `${auj.getUTCFullYear()}-${String(auj.getUTCMonth() + 1).padStart(2, '0')}`;
  const { data, isLoading, error } = useNonPrestes(mois);
  const actions = useActionsConge();
  const [confirmations, setConfirmations] = useState<Record<string, { joursConfirmes: string; ecartMotif: string }>>({});
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  const confirmer = () =>
    executer(
      () => actions.confirmerNonPrestes.mutateAsync({ mois, lignes: (data?.lignes ?? []).map((l) => ({ id: l.id, joursConfirmes: confirmations[l.id]?.joursConfirmes !== undefined && confirmations[l.id]!.joursConfirmes !== '' ? Number(confirmations[l.id]!.joursConfirmes) : l.jours, ecartMotif: confirmations[l.id]?.ecartMotif || undefined })) }),
      'Déductions confirmées.',
    );
  return (
    <div>
      <PageHeader
        titre="Jours non prestés"
        sousTitre="Absences autorisées du mois, déduites à la paie : état préparé et transmis par les Ressources humaines, déductions confirmées par la paie."
        actions={
          <div className="flex items-center gap-2">
            <Input type="month" value={mois} onChange={(e) => navigate(`/rh/non-prestes/${e.target.value}`)} aria-label="Mois" />
            {rh && (!data?.etat || data.etat.statut === 'PREPARE') && (
              <Button variant="outline" onClick={() => executer(() => actions.preparerNonPrestes.mutateAsync(mois), 'État préparé.')} disabled={actions.preparerNonPrestes.isPending}>
                {data?.etat ? 'Préparer à nouveau' : 'Préparer l’état'}
              </Button>
            )}
            {rh && data?.etat?.statut === 'PREPARE' && (
              <Button onClick={() => executer(() => actions.transmettreNonPrestes.mutateAsync(mois), 'État transmis à la paie.')} disabled={actions.transmettreNonPrestes.isPending}>
                Transmettre à la paie
              </Button>
            )}
            {paie && data?.etat?.statut === 'TRANSMIS' && (
              <Button onClick={confirmer} disabled={actions.confirmerNonPrestes.isPending}>
                Confirmer les déductions
              </Button>
            )}
          </div>
        }
      />
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              État de {mois} {data?.etat ? <Badge variant={data.etat.statut === 'CONFIRME' ? 'default' : 'secondary'}>{LIBELLE_ETAT[data.etat.statut]}</Badge> : <Badge variant="outline">non préparé</Badge>}
            </CardTitle>
            <CardDescription>
              {data?.etat
                ? `Préparé le ${dateHeureFr(data.etat.prepareLe)}${data.etat.transmisLe ? ` · transmis le ${dateHeureFr(data.etat.transmisLe)}` : ''}${data.etat.confirmeLe ? ` · confirmé le ${dateHeureFr(data.etat.confirmeLe)}` : ''}`
                : `${data?.apercu ?? 0} absence(s) autorisée(s) approuvée(s) sur le mois, en attente de préparation.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!data?.lignes.length ? (
              <p className="text-sm text-muted-foreground">Aucune ligne.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Demande</TableHead>
                    <TableHead className="text-right">Jours préparés</TableHead>
                    <TableHead className="text-right">Jours confirmés</TableHead>
                    <TableHead>Écart</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.lignes.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>
                        {l.agent.nom}
                        <div className="text-xs text-muted-foreground">
                          {l.agent.matricule} · {l.agent.structureCode}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Link to={`/rh/conges/${l.congeId}`} className="text-primary hover:underline">
                          {l.conge.numero ?? 'Demande'}
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          du {dateFr(l.conge.dateDebut)} · reprise le {dateFr(l.conge.dateReprise)}
                        </div>
                      </TableCell>
                      <TableCell className="num text-right">{l.jours}</TableCell>
                      <TableCell className="num text-right">
                        {paie && data.etat?.statut === 'TRANSMIS' ? (
                          <Input type="number" min={0} max={31} className="ml-auto w-20 text-right" value={confirmations[l.id]?.joursConfirmes ?? String(l.jours)} onChange={(e) => setConfirmations({ ...confirmations, [l.id]: { joursConfirmes: e.target.value, ecartMotif: confirmations[l.id]?.ecartMotif ?? '' } })} />
                        ) : (
                          (l.joursConfirmes ?? '—')
                        )}
                      </TableCell>
                      <TableCell>
                        {paie && data.etat?.statut === 'TRANSMIS' ? (
                          <Input placeholder="Motif de l'écart" value={confirmations[l.id]?.ecartMotif ?? ''} onChange={(e) => setConfirmations({ ...confirmations, [l.id]: { joursConfirmes: confirmations[l.id]?.joursConfirmes ?? String(l.jours), ecartMotif: e.target.value } })} />
                        ) : (
                          (l.ecartMotif ?? (l.joursConfirmes !== null && l.joursConfirmes !== l.jours ? 'écart' : '—'))
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
